/**
 * Quyền GHI của trình nhập Excel nhân sự, theo TỪNG thực thể.
 *
 * Trước đây là MỘT hằng số `staff.sync` dùng chung cho cả 5 thẻ import, nhưng backend
 * (`data-import-permission.guard.ts`) nay đòi key khác nhau cho từng thực thể. Một hằng số
 * không diễn tả nổi điều đó: role có quyền nhập hợp đồng nhưng không có quyền nhập chấm công
 * sẽ vẫn thấy đủ 5 thẻ rồi ăn 403 khi bấm. Hôm nay cả 5 key do cùng {manager, gd_hr} nắm nên
 * chưa lộ, nhưng đó là trùng hợp chứ không phải thiết kế.
 *
 * Mỗi mục giữ kèm `staff.sync` cũ (ngữ nghĩa OR) cho tới khi PR4 gỡ hẳn.
 */
export const HR_IMPORT_WRITE_PERMISSIONS = {
  employees: ["hrm.employees.sync"],
  contracts: ["hrm.contracts.manage"],
  insurances: ["hrm.insurance.manage"],
  // Cùng key với BHXH thường: ghi vào cùng một chỗ, chỉ khác hình dáng file nguồn.
  insuranceRoster: ["hrm.insurance.manage"],
  attendance: ["hrm.attendance.manage"],
  benefits: ["hrm.benefits.manage"],
  payrolls: ["hrm.payroll.sync"],
  internalDocuments: ["hrm.internal_documents.sync"],
} as const;

/** Hợp của mọi quyền trên — dùng cho cổng vào trang /imports (thấy được ÍT NHẤT một thẻ). */
export const HR_IMPORT_ANY_WRITE_PERMISSION: readonly string[] = [
  ...new Set(Object.values(HR_IMPORT_WRITE_PERMISSIONS).flat()),
];

export const ROUTES = {
  root: "/",
  dashboard: "/dashboard",
  notifications: "/notifications",

  auth: {
    login: "/auth/login",
    otp: "/auth/otp",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    register: "/auth/register",
    selectStore: "/auth/select-store",
    changePassword: "/auth/change-password",
    /** EG-356 Sprint 6 (04b9120) — đặt password lần đầu sau register OTP */
    setPassword: "/auth/set-password",
  },

  pos: "/pos",
  posCustomerDisplay: "/pos/customer-display",
  posReturns: "/pos/returns",
  posBuyIn: "/pos/buy-in",
  posShifts: {
    list: "/pos/shifts",
    detail: (id: string) => `/pos/shifts/${id}`,
  },

  buyback: {
    list: "/buyback",
    new: "/buyback/new",
    detail: (id: string) => `/buyback/${id}`,
    reports: "/reports/buyback",
  },

  exchanges: {
    list: "/exchanges",
    new: "/exchanges/new",
    detail: (id: string) => `/exchanges/${id}`,
    approve: (id: string) => `/exchanges/${id}/approve`,
    reports: "/reports/exchange",
  },

  products: {
    list: "/products",
    new: "/products/new",
    detail: (id: string) => `/products/${id}`,
    edit: (id: string) => `/products/${id}/edit`,
    units: "/products/units",
    combos: "/products/combos",
    combosNew: "/products/combos/new",
    combosEdit: (id: string) => `/products/combos/${id}/edit`,
    combosFix: "/products/combos-fix",
    combosFixNew: "/products/combos-fix/new",
    combosFixDetail: (id: string) => `/products/combos-fix/${id}`,
    combosFixEdit: (id: string) => `/products/combos-fix/${id}/edit`,
    categories: {
      list: "/products/categories",
    },
    metalCategories: {
      list: "/products/metal-categories",
    },
    brands: {
      list: "/products/brands",
    },
    attributes: {
      list: "/products/attributes",
    },
  },
  services: {
    list: "/services",
    new: "/services/new",
    detail: (id: string) => `/services/${id}`,
    edit: (id: string) => `/services/${id}/edit`,
  },
  orders: {
    list: "/orders",
    posList: "/orders?channel_group=pos&date_preset=this_month&page=1",
    detail: (id: string) => `/orders/${id}`,
    fulfillmentWavePacking: "/orders/fulfillment-wave-packing",
    imports: "/orders/imports",
  },
  ecom: {
    index: "/ecom",
    orders: "/ecom/orders",
    reports: "/ecom/reports",
    performance: "/ecom/performance",
    productsReport: "/ecom/products-report",
    salesStats: "/ecom/sales-stats",
    revenueReport: "/ecom/revenue-report",
    expenseReport: "/ecom/expense-report",
    returnCancelTracking: "/ecom/return-cancel-tracking",
    // TCR-2481 — Báo cáo "Nhân viên" (Thống kê nhân viên), gate 'ecom.reports.view'.
    staffStats: "/ecom/staff-stats",
    // TCR-2094 — Báo cáo ngày TikTok Shop (Task 7): GMV/NMV/số đơn tự động + Views/Visits/Ads
    // nhập tay. Gate bằng 'ecom.reports.view' giống các trang báo cáo Ecom khác ở trên.
    tiktokDailyReport: "/ecom/tiktok-daily-report",
    // TCR-2095 — Báo cáo ngày Shopee (Task 8), gate bằng 'ecom.reports.view' giống trang TikTok.
    shopeeDailyReport: "/ecom/shopee-daily-report",
    cancelOrders: "/ecom/cancel-orders",
    returns: "/ecom/returns",
    reconciliation: "/ecom/reconciliation",
    orderDetail: (id: string) => `/ecom/orders/${id}`,
    orderNew: "/ecom/orders/new",
    channels: "/ecom/settings?tab=channels",
    settings: "/ecom/settings",
    // TCR-2094/2095 — màn quản lý BusinessBrand (nhãn hàng kinh doanh TikTok Shop/Shopee),
    // nền tảng cho filter Brand ở Báo cáo ngày TikTok/Shopee (Task 7/8).
    businessBrands: "/ecom/settings/business-brands",
    oauthResult: "/ecom/oauth-result",
    products: "/ecom/settings?tab=product-links",
    buyers: "/ecom/buyers",
    finance: "/ecom/finance",
    shipping: "/ecom/shipping",
    fulfillment: {
      initLogistic: "/ecom/fulfillment/init-logistic",
      orderPacking: "/ecom/fulfillment/order-packing",
      wavePacking: "/ecom/fulfillment-wave-packing",
      orderSending: "/ecom/fulfillment/order-sending",
      settings: "/ecom/fulfillment/settings",
      validate: "/ecom/fulfillment/validate",
      preparing: "/ecom/fulfillment/preparing",
      packing: "/ecom/fulfillment/packing",
      handover: "/ecom/fulfillment/handover",
      stockIssue: "/ecom/fulfillment/stock-issue",
      delivery: "/ecom/fulfillment/delivery",
      return: "/ecom/fulfillment/return",
      reconciliation: "/ecom/fulfillment/reconciliation",
      exceptions: "/ecom/fulfillment/exceptions",
    },
  },
  b2b: {
    index: "/b2b",
    pricing: {
      list: "/b2b/pricing",
      tab: (tab: string) =>
        tab && tab !== "list" ? `/b2b/pricing?tab=${tab}` : "/b2b/pricing",
      new: "/b2b/pricing/new",
      detail: (id: string) => `/b2b/pricing/${id}`,
      edit: (id: string) => `/b2b/pricing/${id}/edit`,
      overrideNew: "/b2b/pricing/overrides/new",
      overrideDetail: (id: string) => `/b2b/pricing/overrides/${id}`,
      overrideEdit: (id: string) => `/b2b/pricing/overrides/${id}/edit`,
    },
    rebates: {
      list: "/b2b/rebates",
      tab: (tab: string) =>
        tab && tab !== "policies" ? `/b2b/rebates?tab=${tab}` : "/b2b/rebates",
      new: "/b2b/rebates/new",
      detail: (id: string) => `/b2b/rebates/${id}`,
      edit: (id: string) => `/b2b/rebates/${id}/edit`,
      ledgerDetail: (customerId: string) => `/b2b/rebates/ledger/${customerId}`,
    },
    receivables: "/b2b/receivables",
    receivableCustomerDetail: (id: string) =>
      `/b2b/receivables/customers/${id}`,
    giftDebts: "/b2b/gift-debts",
    creditApprovals: "/b2b/credit-approvals",
    comboGifts: "/b2b/combo-gifts",
    crm: "/b2b/crm",
    customers: "/b2b/customers",
    customer360: (id: string) => `/b2b/crm/customers/${id}`,
    customerNew: "/b2b/customers/new",
    customerGroups: "/b2b/customers/groups",
    executiveDashboard: "/b2b/executive-dashboard",
    settings: "/b2b/settings",
    staffInventory: {
      list: "/b2b/staff-inventory",
      detail: (id: string) => `/b2b/staff-inventory/${id}`,
    },
  },
  inventory: {
    list: "/inventory",
    receipts: "/inventory/receipts",
    receiptNew: "/inventory/receipts/new",
    receiptDetail: (id: string) => `/inventory/receipts/${id}`,
    receiptEdit: (id: string) => `/inventory/receipts/${id}/edit`,
    issues: "/inventory/issues",
    issueNew: "/inventory/issues/new",
    issueDetail: (id: string) => `/inventory/issues/${id}`,
    issueEdit: (id: string) => `/inventory/issues/${id}/edit`,
    transfers: "/inventory/transfers",
    transferNew: "/inventory/transfers/new",
    transferDetail: (id: string) => `/inventory/transfers/${id}`,
    transferEdit: (id: string) => `/inventory/transfers/${id}/edit`,
    transferApprove: (id: string) => `/inventory/transfers/${id}/approve`,
    counts: "/inventory/counts",
    countNew: "/inventory/counts/new",
    countEnter: (id: string) => `/inventory/counts/${id}/enter`,
    countVariance: (id: string) => `/inventory/counts/${id}/variance`,
    movements: "/inventory/movements",
    alerts: "/inventory/alerts",
    costing: "/inventory/costing",
    costExceptions: "/inventory/cost-exceptions",
    printLabels: "/inventory/print-labels",
    warehouses: "/inventory/warehouses",
    warehouseNew: "/inventory/warehouses/new",
    warehouseLocations: "/inventory/warehouse-locations",
    lots: "/inventory/lots",
    // TCR-1517 Task 7 — Lệnh lắp ráp / tháo dỡ combo thật.
    assemblyOrders: "/inventory/assembly-orders",
    recalls: "/inventory/recalls",
    recallNew: "/inventory/recalls/new",
    recallDetail: (id: string) => `/inventory/recalls/${id}`,
  },
  staff: {
    list: "/staff",
    detail: (id: string) => `/staff/${id}`,
    invite: "/staff/invite",
    activity: (id: string) => `/staff/${id}/activity`,
    stores: (id: string) => `/staff/${id}/stores`,
    // EG-510 — Payroll Profile tab for staff detail
    payrollProfile: (id: string) => `/staff/${id}/payroll-profile`,
  },
  hrm: {
    dashboard: "/hrm",
    basevn: "/hrm/basevn",
    basevnSyncLog: "/hrm/basevn-sync-log",
    attendancePolicy: "/hrm/attendance-policy",
    attendanceConfig: "/hrm/attendance-config",
    settings: "/hrm/settings",
    employees: "/hrm/employees",
    /** Hồ sơ nhân sự của chính người đang đăng nhập (đề xuất sửa thông tin cá nhân). */
    myProfile: "/me",
    employeeChangeRequests: "/hrm/employee-change-requests",
    employeeDetail: (id: string) => `/hrm/employees/${id}`,
    employeeNew: "/hrm/employees/new",
    employeeEdit: (id: string) => `/hrm/employees/${id}/edit`,
    /*
     * Không còn hằng số `departments` / `subDepartments`: hai trang đó đã gộp vào
     * cây "Cơ cấu tổ chức" ở `/hrm/positions`. Hai đường dẫn cũ vẫn sống trong
     * App.tsx dưới dạng `<Navigate>` để link cũ và bookmark không gãy, nhưng
     * không còn chỗ nào ĐIỀU HƯỚNG tới chúng nữa — giữ hằng số lại chỉ mời người
     * sau trỏ vào một trang không tồn tại.
     */
    offices: "/hrm/offices",
    positions: "/hrm/positions",
    positionDetail: (id: string) => `/hrm/positions/${id}`,
    legalEntities: "/hrm/legal-entities",
    legalEntityDetail: (id: string) => `/hrm/legal-entities/${id}`,
    assetTypes: "/hrm/asset-types",
    orgChart: "/hrm/org-chart",
    internalDocuments: "/hrm/internal-documents",
    documentTypes: "/hrm/document-types",
    workRegions: "/hrm/work-regions",
    internalDocumentNew: "/hrm/internal-documents/new",
    internalDocumentImport: "/hrm/internal-documents/import",
    internalDocumentDetail: (id: string) => `/hrm/internal-documents/${id}`,
    internalDocumentEdit: (id: string) => `/hrm/internal-documents/${id}/edit`,
    contracts: "/hrm/contracts",
    contractNew: "/hrm/contracts/new",
    contractDetail: (id: string) => `/hrm/contracts/${id}`,
    contractEdit: (id: string) => `/hrm/contracts/${id}/edit`,
    kpis: "/hrm/kpis",
    kpiScorecardImport: "/hrm/kpis/import",
    payrolls: "/hrm/payrolls",
    benefits: "/hrm/benefits",
    insuranceRoster: "/hrm/insurance",
    attendances: "/hrm/attendances",
    /*
     * Quy trình và Công việc đã tách sang phân hệ E-Office (`ROUTES.eOffice`).
     * Hai khoá BÁO CÁO vẫn ở đây vì chúng nằm trong nhóm Báo cáo của Nhân sự,
     * cạnh báo cáo chấm công / nghỉ phép.
     */
    workflowsReport: "/hrm/reports/workflows",
    weworksReport: "/hrm/reports/weworks",
    requests: "/hrm/requests",
    requestDetail: (id: string) => `/hrm/requests/${id}`,
    requestsReport: "/hrm/reports/requests",
    schedules: "/hrm/schedules",
    overtime: "/hrm/overtime",
    schedulesReport: "/hrm/reports/schedules",
    timeoffs: "/hrm/timeoffs",
    timeoffsReport: "/hrm/reports/timeoffs",
    annualLeaveReport: "/hrm/reports/annual-leave",
    turnoverReport: "/hrm/reports/turnover",
    trendReport: "/hrm/reports/trend",
    /** TCR-2364 — file/link báo cáo cho lãnh đạo xem. */
    policyReportDocuments: "/hrm/reports/policy-documents",
    recruitment: {
      list: "/hrm/recruitment",
      /**
       * Danh sách ứng viên TOÀN TENANT, không gắn một yêu cầu nào.
       *
       * Phải là route TĨNH đặt trước `:id` trong App.tsx, nếu không "candidates" bị bắt làm id
       * yêu cầu và vào màn chi tiết.
       */
      candidates: "/hrm/recruitment/candidates",
      /** Tạo ứng tuyển từ màn Ứng viên, chọn vị trí trong form (TCR-2343). Cũng TĨNH, trước `:id`. */
      candidateCreate: "/hrm/recruitment/candidates/new",
      /** Hộp tờ trình duyệt ứng viên. Cũng là route TĨNH, phải đứng trước `:id`. */
      approvals: "/hrm/recruitment/approvals",
      new: "/hrm/recruitment/new",
      detail: (id: string) => `/hrm/recruitment/${id}`,
      edit: (id: string) => `/hrm/recruitment/${id}/edit`,
      candidateNew: (requestId: string) =>
        `/hrm/recruitment/${requestId}/candidates/new`,
      candidateEdit: (requestId: string, candidateId: string) =>
        `/hrm/recruitment/${requestId}/candidates/${candidateId}/edit`,
    },
  },
  reports: {
    index: "/reports",
    revenue: "/reports/revenue",
    inventory: "/reports/inventory",
    eod: "/reports/eod",
    pawn: "/reports/pawn",
    installment: "/reports/installment",
    workshop: "/reports/workshop",
    exchange: "/reports/exchange",
    buyback: "/reports/buyback",
    accounting: "/reports/accounting",
    businessResults: "/reports/business-results",
    balanceSheet: "/reports/balance-sheet",
    trialBalance: "/reports/trial-balance",
    s2bRevenue: "/reports/s2b-revenue",
    s2cIncomeExpense: "/reports/s2c-income-expense",
    s2dMaterials: "/reports/s2d-materials",
    s2eCash: "/reports/s2e-cash",
    inventorySummary: "/reports/inventory-summary",
    cashBook: "/reports/cash-book",
    documentListing: "/reports/document-listing",
    voucherListing: "/reports/voucher-listing",
    invoiceListing: "/reports/invoice-listing",
    warehouseVoucherListing: "/reports/warehouse-voucher-listing",
    receivablePayableListing: "/reports/receivable-payable-listing",
  },
  /**
   * TCR-2542 — Trung tâm báo cáo (khung báo cáo mới + nivo). Danh mục báo cáo và
   * trang chi tiết; nội dung từng báo cáo khai trong
   * `src/pages/ReportCenter/reportRegistry.ts` (thêm 1 dòng/báo cáo).
   */
  reportCenter: {
    index: "/report-center",
    report: (id: string) => `/report-center/${id}`,
  },
  /** EG-504..EG-529 — Payroll module (Sprint 5.1) */
  /**
   * App rút gọn cho người giao hàng (TCR-1521).
   *
   * Nằm NGOÀI nhánh `/` của Layout ERP: vỏ riêng, thanh tab dưới cùng, không có
   * thanh bên. Xem `pages/ShipperApp/ShipperLayout.tsx`.
   */
  shipper: {
    root: "/shipper",
    orders: "/shipper/orders",
    claimOrders: "/shipper/orders/claim",
    orderDetail: (id: string) => `/shipper/orders/${id}`,
    cod: "/shipper/cod",
    notifications: "/shipper/notifications",
    profile: "/shipper/profile",
    deliveryStats: "/shipper/profile/statistics",
    codStats: "/shipper/profile/cod-statistics",
    codReconciliation: "/shipper/profile/cod-reconciliation",
  },

  payroll: {
    dashboard: "/payroll",
    components: "/payroll/components",
    runs: "/payroll/runs",
    runDetail: (id: string) => `/payroll/runs/${id}`,
    payslips: "/payroll/payslips",
    settings: "/payroll/settings",
    reports: {
      s5hkd: "/payroll/reports/s5-hkd",
      qttTncn: "/payroll/reports/qtt-tncn",
    },
  },
  customers: {
    list: "/customers",
    groups: "/customers/groups",
    new: "/customers/new",
    detail: (id: string) => `/customers/${id}`,
    edit: (id: string) => `/customers/${id}/edit`,
    purchases: (id: string) => `/customers/${id}/purchases`,
    loyalty: (id: string) => `/customers/${id}/loyalty`,
    credits: (id: string) => `/customers/${id}/credits`,
  },
  suppliers: {
    list: "/suppliers",
    new: "/suppliers/new",
    detail: (id: string) => `/suppliers/${id}`,
    history: (id: string) => `/suppliers/${id}/history`,
  },
  salesInvoices: {
    list: "/sales/invoices",
    new: "/sales/invoices/new",
    detail: (id: string) => `/sales/invoices/${id}`,
    edit: (id: string) => `/sales/invoices/${id}/edit`,
  },
  purchasing: {
    list: "/accounting/purchasing",
    new: "/accounting/purchasing/new",
    detail: (id: string) => `/accounting/purchasing/${id}`,
    edit: (id: string) => `/accounting/purchasing/${id}/edit`,
    receive: (id: string) => `/accounting/purchasing/${id}/receive`,
    receivingList: "/accounting/purchasing/receiving",
    match: (id: string) => `/accounting/purchasing/${id}/match`,
    reports: "/accounting/purchasing/reports",
    apSummary: "/accounting/purchasing/reports/ap-summary",
    apDetail: "/accounting/purchasing/reports/ap-detail",
    purchaseLedger: "/accounting/purchasing/reports/purchase-ledger",
  },
  promotions: {
    list: "/promotions",
    new: "/promotions/new",
    edit: (id: string) => `/promotions/${id}/edit`,
    vouchers: "/promotions/vouchers",
    voucherNew: "/promotions/vouchers/create",
    voucherEdit: (id: string) => `/promotions/vouchers/${id}/edit`,
    flashSales: "/promotions/flash-sales",
    flashSaleNew: "/promotions/flash-sales/create",
    flashSaleEdit: (id: string) => `/promotions/flash-sales/${id}/edit`,
    loyalty: "/promotions/loyalty",
    wholesaleGifts: "/promotions/wholesale-gifts",
    reports: "/promotions/reports",
  },
  workshop: {
    list: "/workshop",
    new: "/workshop/new",
    detail: (id: string) => `/workshop/${id}`,
    craftsmen: "/workshop/craftsmen",
    services: "/workshop/services",
    commissions: "/workshop/commissions",
    reports: "/reports/workshop",
  },
  commission: {
    index: "/commissions",
    my: "/commissions/my",
    kpi: "/commissions/kpi",
    kpiTargets: "/commissions/kpi-targets",
    leaderboard: "/commissions/leaderboard",
    config: "/commissions/config",
    configGeneral: "/commissions/config/general",
    roles: "/commissions/config/roles",
    productRates: "/commissions/config/product-rates",
    bonusThresholds: "/commissions/config/bonus-thresholds",
    adjustments: "/commissions/adjustments",
    payouts: "/commissions/payouts",
    liveSessions: "/commissions/live-sessions",
    reports: "/commissions/reports",
    review: "/commissions/review",
    reviewDetail: (period: string, staffId: string) =>
      `/commissions/review/${period}/${staffId}`,
  },
  onboarding: {
    index: "/onboarding",
    business: "/onboarding/business",
    store: "/onboarding/store",
    staff: "/onboarding/staff",
    products: "/onboarding/products",
    payment: "/onboarding/payment",
    invoice: "/onboarding/invoice",
    complete: "/onboarding/complete",
  },
  setup: {
    index: "/setup",
    branding: "/setup/branding",
    catalog: "/setup/catalog",
    plugin: "/setup/plugin",
    hardware: "/setup/hardware",
    demo: "/setup/demo",
    complete: "/setup/complete",
  },
  pawn: {
    list: "/pawn",
    new: "/pawn/new",
    detail: (id: string) => `/pawn/${id}`,
    dueToday: "/pawn/due-today",
    overdue: "/pawn/overdue",
    settings: "/pawn/settings",
    reports: "/reports/pawn",
  },
  installment: {
    list: "/installment",
    new: "/installment/new",
    detail: (id: string) => `/installment/${id}`,
    collect: (id: string) => `/installment/${id}/collect`,
    collections: "/installment/collections",
    schedule: (id: string) => `/installment/${id}/schedule`,
    settings: "/installment/settings",
    reports: "/reports/installment",
  },
  consignment: {
    list: "/consignment",
    new: "/consignment/new",
    detail: (id: string) => `/consignment/${id}`,
    payout: (id: string) => `/consignment/${id}/payout`,
    reports: "/consignment/reports",
  },
  labels: {
    list: "/labels",
    new: "/labels/new",
    edit: (id: string) => `/labels/${id}/edit`,
    preview: (id: string) => `/labels/${id}/preview`,
    print: "/labels/print",
    printerSettings: "/labels/printer-settings",
  },
  finance: {
    eod: "/finance/eod",
    cashBook: "/finance/cash-book",
    vouchers: "/finance/vouchers",
    vouchersNew: "/finance/vouchers/new",
    invoices: "/finance/invoices",
    invoicesIssue: "/finance/invoices/issue",
    invoiceDetail: (id: string) => `/finance/invoices/${id}`,
    invoiceAdjust: (id: string) => `/finance/invoices/${id}/adjust`,
    eodDetail: (date: string) => `/finance/eod/${date}`,
    einvoiceSettings: "/finance/einvoice-settings",
    einvoiceList: "/finance/einvoices",
    einvoiceDetail: (id: string) => `/finance/einvoices/${id}`,
    einvoiceNew: "/finance/einvoices/new",
    paymentMethods: "/finance/payment-methods",
  },
  stores: {
    list: "/stores",
    new: "/stores/new",
    detail: (id: string) => `/stores/${id}/edit`,
    edit: (id: string) => `/stores/${id}/edit`,
    hardware: (id: string) => `/stores/${id}/hardware`,
  },
  reportsAdvanced: {
    profit: "/reports/profit",
    staff: "/reports/staff",
    customers: "/reports/customers",
    products: "/reports/products",
    multiStore: "/reports/multi-store",
    builder: "/reports/builder",
    einvoiceStats: "/finance/einvoice/reports/stats",
    einvoiceTax: "/finance/einvoice/reports/tax",
  },
  /**
   * E-Office — Quy trình (workflow) và Công việc (wework).
   *
   * Trước đây nằm trong menu Nhân sự ở `/hrm/workflows` và `/hrm/weworks`.
   * Đường dẫn cũ vẫn chuyển hướng sang đây (xem `App.tsx`) để link đã gửi đi
   * và dấu trang của người dùng không chết.
   */
  eOffice: {
    workflows: "/e-office/workflows",
    workflowDetail: (id: string) => `/e-office/workflows/${id}`,
    workflowBoard: (workflowId: string) =>
      `/e-office/workflows/board/${workflowId}`,
    weworks: "/e-office/weworks",
    weworkDetail: (projectId: string) => `/e-office/weworks/${projectId}`,
  },
  imports: {
    index: "/import",
    products: "/import/products",
    customers: "/import/customers",
    suppliers: "/import/suppliers",
    employees: "/import/employees",
    contracts: "/import/contracts",
    insurances: "/import/insurances",
    insuranceRoster: "/import/insurance-roster",
    attendance: "/import/attendance",
    benefits: "/import/benefits",
    payrolls: "/import/payrolls",
    /** Chi tiết một lần nhập — bấm vào dòng trong bảng Lịch sử import. */
    jobDetail: (id: string) => `/import/jobs/${id}`,
  },
  // EG-348 Phase D — CRM 360 (port từ inventory legacy UI)
  crm: {
    dashboard: "/crm",
    followUps: "/crm/follow-ups",
  },
  referral: {
    overview: "/referral",
    list: "/referral/list",
    leaderboard: "/referral/leaderboard",
    config: "/referral/config",
  },
  audit: {
    log: "/settings/audit-log",
    apiKeys: "/settings/api-keys",
  },
  system: {
    actionLogs: "/settings/action-logs",
  },
  shipping: {
    list: "/shipping",
    new: "/shipping/new",
    detail: (id: string) => `/shipping/${id}`,
    codReconciliation: "/shipping/cod-reconciliation",
    codReconciliationDetail: (id: string) =>
      `/shipping/cod-reconciliation/${id}`,
    /** Nộp tiền thu hộ của NGƯỜI GIAO — khác đối soát với ĐVVC ở trên (TCR-1521). */
    codRemittances: "/shipping/cod-remittances",
    pickupLocations: "/shipping/pickup-locations",
    ghtkLogs: "/shipping/ghtk-logs",
    logs: "/shipping/logs",
    map: "/shipping/map",
  },
  support: {
    // TCR-1743 — `home: '/support'` đã gỡ cùng lúc với route index trong App.tsx và mục
    // sidebar "Trung tâm hỗ trợ". Giữ hằng ở đây sẽ khiến chỗ khác điều hướng tới một
    // route không còn tồn tại (ra NotFound) mà TypeScript không báo gì.
    changelog: "/support/changelog",
  },
  accounting: {
    dashboard: "/accounting/dashboard",
    transactions: "/accounting/transactions",
    cash: "/accounting/cash",
    bankDeposits: "/accounting/bank-deposits",
    receiptNew: "/accounting/transactions/receipt/new",
    receiptEdit: (id: string) => `/accounting/transactions/receipt/${id}`,
    paymentNew: "/accounting/transactions/payment/new",
    paymentEdit: (id: string) => `/accounting/transactions/payment/${id}`,
    internalTransferNew: "/accounting/transactions/internal-transfer/new",
    internalTransferEdit: (id: string) =>
      `/accounting/transactions/internal-transfer/${id}`,
    prepaidExpenses: "/accounting/transactions/prepaid-expenses",
    prepaidExpenseNew: "/accounting/transactions/prepaid-expenses/new",
    prepaidExpenseEdit: (id: string) =>
      `/accounting/transactions/prepaid-expenses/${id}`,
    fixedAssets: "/accounting/transactions/fixed-assets",
    fixedAssetNew: "/accounting/transactions/fixed-assets/new",
    fixedAssetEdit: (id: string) =>
      `/accounting/transactions/fixed-assets/${id}`,
    fixedAssetDetail: (id: string) =>
      `/accounting/transactions/fixed-assets/${id}`,
    fixedAssetGroups: "/accounting/transactions/fixed-asset-groups",
    toolSupplies: "/accounting/transactions/tool-supplies",
    toolSupplyNew: "/accounting/transactions/tool-supplies/new",
    toolSupplyReports: "/accounting/transactions/tool-supplies/reports",
    toolSupplyEdit: (id: string) =>
      `/accounting/transactions/tool-supplies/${id}`,
    toolSupplyDetail: (id: string) =>
      `/accounting/transactions/tool-supplies/${id}`,
    otherDocument: "/accounting/transactions/other",
    // AMIS-style routes
    giftOrders: "/accounting/sales/gift-orders",
    salesDocuments: "/sales/documents",
    salesDocumentNew: "/sales/documents/new",
    salesDocumentDetail: (id: string) => `/sales/documents/${id}`,
    salesDocumentReturnsNew: "/sales/documents/returns/new",
    salesDocumentDiscountsNew: "/sales/documents/discounts/new",
    salesDocumentReturnDetail: (id: string) => `/sales/documents/returns/${id}`,
    salesDocumentReturnEdit: (id: string) =>
      `/sales/documents/returns/${id}/edit`,
    salesDocumentDiscountDetail: (id: string) =>
      `/sales/documents/discounts/${id}`,
    salesDocumentDiscountEdit: (id: string) =>
      `/sales/documents/discounts/${id}/edit`,
    salesEinvoices: "/sales/e-invoices",
    accountingEInvoices: "/accounting/e-invoices",
    eInvoiceReplacementNew: "/accounting/e-invoices/new?mode=replacement",
    eInvoiceReplacementEdit: (id: string) =>
      `/accounting/e-invoices/new?mode=replacement&edit_id=${encodeURIComponent(id)}`,
    // Legacy aliases — keep for backward compat
    salesInvoices: "/sales/invoices",
    salesInvoiceNew: "/sales/invoices/new",
    adjustmentInvoices: "/sales/invoices/adjustment",
    replacementInvoices: "/sales/invoices/replacement",
    salesReturns: "/sales/invoices/returns",
    purchaseInvoices: "/accounting/purchase-invoices",
    purchaseInvoiceDetail: (id: string) => `/accounting/purchase-invoices/${id}`,
    purchaseDiscounts: "/accounting/purchase-discounts",
    purchaseDiscountNew: "/accounting/purchase-discounts/new",
    purchaseDiscountDetail: (id: string) =>
      `/accounting/purchase-discounts/${id}`,
    purchaseDiscountEdit: (id: string) =>
      `/accounting/purchase-discounts/${id}/edit`,
    purchaseInvoiceNew: "/accounting/purchase-invoices/new",
    purchaseReturns: "/accounting/purchase-returns",
    purchaseReturnNew: "/accounting/purchase-returns/new",
    govInvoices: "/accounting/gov-invoices",
    govInvoiceDetail: (id: string) => `/accounting/gov-invoices/${id}`,
    inventoryReceipts: "/accounting/inventory/receipts",
    inventoryIssues: "/accounting/inventory/issues",
    inventoryTransfers: "/accounting/inventory/transfers",
    productsUom: "/accounting/inventory/products-uom",
    customersAR: "/accounting/customers/ar",
    suppliersAP: "/accounting/suppliers/ap",
    debtSettlements: {
      customerDocumentOffset:
        "/accounting/debt-settlements/customer-document-offset",
      customerDocumentOffsetMulti:
        "/accounting/debt-settlements/customer-document-offset-multi",
      customerDebtOffset: "/accounting/debt-settlements/customer-debt-offset",
      removeCustomerDocumentOffset:
        "/accounting/debt-settlements/remove-customer-document-offset",
    },
    reports: "/reports/accounting",
    reportBusinessResults: "/reports/business-results",
    reportBalanceSheet: "/reports/balance-sheet",
    reportTrialBalance: "/reports/trial-balance",
    reportS2b: "/reports/s2b-revenue",
    reportS2c: "/reports/s2c-income-expense",
    reportS2d: "/reports/s2d-materials",
    reportS2e: "/reports/s2e-cash",
    reportInventorySummary: "/reports/inventory-summary",
    reportCashBook: "/reports/cash-book",
    reportBankDepositBook: "/reports/bank-deposit-book",
    reportJournalEntries: "/reports/journal-entries",
    reportJournalEntryDetail: (id: string) => `/reports/journal-entries/${id}`,
    reportDocumentListing: "/reports/document-listing",
    reportVoucherListing: "/reports/voucher-listing",
    reportInvoiceListing: "/reports/invoice-listing",
    reportWarehouseVoucher: "/reports/warehouse-voucher-listing",
    reportReceivablePayable: "/reports/receivable-payable-listing",
    reportCustomersAR: "/reports/customers-ar",
    reportArSummary: "/reports/ar-summary",
    reportArDetail: "/reports/ar-detail",
    reportSalesSummaryByProduct: "/reports/sales-summary-by-product",
    reportSalesDetail: "/reports/sales-detail",
    settings: "/settings/accounting",
    dataRepair: "/accounting/data-repair",
    documentScenarios: "/accounting/document-scenarios",
    documentScenarioTest: "/accounting/document-scenarios/test",
    amisConnect: "/accounting/amis/connect",
    amisTasks: "/accounting/amis/connect?section=tasks",
    amisOrgUnits: "/accounting/amis/org-units",
    amisStocks: "/accounting/amis/stocks",
    amisInventoryItems: "/accounting/amis/inventory-items",
    amisAccountObjects: "/accounting/amis/account-objects",
    // Path số ít (không phải "bank-accounts") — khớp catalog slug "bank-account" đã dùng
    // để kích đồng bộ tay (`AmisMasterdataCatalog`), 1 giá trị dùng chung cho cả liệt kê
    // lẫn kích đồng bộ.
    amisBankAccounts: "/accounting/amis/bank-account",
    amisSyncLogs: "/accounting/amis/sync-logs",
    amisOutbox: "/accounting/amis/outbox",
    amisOutboxDetail: (id: string) => `/accounting/amis/outbox/${id}`,
    // TCR-2523 pha 2 — kéo tồn kho từ AMIS + sinh file số dư đầu kỳ (chỉ đọc).
    amisInventoryBalance: "/accounting/amis/inventory-balance",
  },

  settings: {
    index: "/settings",
    business: "/settings/business",
    appearance: "/settings/appearance",
    hardware: "/settings/hardware",

    roles: "/settings/roles",
    /**
     * TCR-1893 — cùng một trang phục vụ cả "Xem chi tiết" (read-only) lẫn
     * "Chỉnh sửa"; `?mode=edit` là thứ phân biệt hai chế độ.
     */
    roleDetail: (id: string, opts?: { edit?: boolean }) =>
      opts?.edit ? `/settings/roles/${id}?mode=edit` : `/settings/roles/${id}`,
    profile: "/settings/profile",
    security: "/settings/security",
    notifications: "/settings/notifications",
    general: "/settings/general",
    locale: "/settings/locale",
    dataExport: "/settings/data-export",
    // EG-345 P4 — wire mới (P3 einvoice + payment dùng ROUTES.finance.*)
    apiKeys: "/settings/api-keys",
    auditLog: "/settings/audit-log",
    migrations: "/settings/migrations",
    workerMonitor: "/settings/worker-monitor",
    // EG-508 — Payroll settings
    payroll: "/settings/payroll",
    receiptTemplates: "/settings/receipt-templates",
    receiptTemplateNew: "/settings/receipt-templates/new",
    receiptTemplateEdit: (id: string) =>
      `/settings/receipt-templates/${id}/edit`,
    accounting: "/settings/accounting",
  },
  // EG-520 — Payslip history (self-service)
  me: {
    payslips: "/me/payslips",
  },
  omnichat: {
    inbox: "/omnichat",
    connections: "/omnichat/connections",
    care: "/omnichat/care",
    careRules: "/omnichat/settings/care-rules",
    campaigns: "/omnichat/campaigns",
    slaReport: "/omnichat/reports/sla",
    campaignNew: "/omnichat/campaigns/new",
    campaign: (id: string) => `/omnichat/campaigns/${id}`,
    settings: "/omnichat/settings",
  },
} as const;

/** Legacy: keep `login` top-level for back-compat (= ROUTES.auth.login). */
export const LEGACY_LOGIN = ROUTES.auth.login;

/**
 * TCR-685 — Voucher create/edit screens (phiếu thu / phiếu chi / chuyển tiền nội bộ)
 * live under /accounting/transactions/*, but are launched from the Tiền mặt / Tiền gửi /
 * Tất cả thu chi menu tabs. A `from` query param (cash | bank | all) records the origin
 * tab so the form's Back / Hủy / after-save returns there and the sidebar keeps that tab
 * highlighted, instead of always jumping to "Tất cả thu chi".
 */
export function accountingVoucherReturnRoute(from?: string | null): string {
  if (from === "cash") return ROUTES.accounting.cash;
  if (from === "bank") return ROUTES.accounting.bankDeposits;
  return ROUTES.accounting.transactions;
}

/** Voucher-create route prefixes that may be launched from a Tiền mặt / Tiền gửi tab. */
const ACCOUNTING_VOUCHER_CREATE_PREFIXES = [
  "/accounting/transactions/receipt/",
  "/accounting/transactions/payment/",
  "/accounting/transactions/internal-transfer/",
];

/**
 * TCR-685 — When the current route is a voucher-create screen opened from the Tiền mặt or
 * Tiền gửi tab (recorded via `?from=cash|bank`), return that tab's route so the sidebar and
 * the top section-tab strip keep it highlighted. Returns null to fall back to the default
 * path-based selection (which resolves to "Tất cả thu chi").
 */
export function accountingVoucherSourceRoute(
  pathname: string,
  search: string,
): string | null {
  const cleanPath = pathname.replace(/\/+$/, "") + "/";
  if (
    !ACCOUNTING_VOUCHER_CREATE_PREFIXES.some((prefix) =>
      cleanPath.startsWith(prefix),
    )
  )
    return null;
  const from = new URLSearchParams(search).get("from");
  if (from === "cash") return ROUTES.accounting.cash;
  if (from === "bank") return ROUTES.accounting.bankDeposits;
  return null;
}

/**
 * The receiving detail screen is nested below the purchase-order route for
 * data/navigation purposes, but it belongs to the "Nhận hàng mua" section.
 * Keep the top section navigation on that tab while a user is receiving a PO.
 */
export function purchasingReceivingSourceRoute(
  pathname: string,
): string | null {
  const cleanPath = pathname.replace(/\/+$/, "");
  const receivingDetailPattern = /^\/accounting\/purchasing\/[^/]+\/receive$/;
  return receivingDetailPattern.test(cleanPath)
    ? ROUTES.purchasing.receivingList
    : null;
}
